// Third-party
import React from 'react';
import { Options as ChartOptions, SeriesOptionsType } from 'highcharts';
import classNames from 'classnames';
import Locale from '@/types/locale';
import { LOCALE } from '../../../utils/i18n';

// Utils
import { FormattedMessage, formatPercentage } from '../../../utils/i18n';

type Props = {
  chartOptions: ChartOptions;
  id?: string;
  className?: string;
  sort?: boolean;
  showTotal?: boolean;
  totalLabel?: string;
  locale?: Locale;
};

const getData = (chartOptions: ChartOptions, sort: boolean) => {
  if (!chartOptions.series || chartOptions.series.length === 0) {
    return [];
  }

  const data = chartOptions.series[0].data.slice() as SeriesOptionsType[];
  return sort ? data.sort((d1, d2) => d2.y - d1.y) : data;
};

const getThumbnailStyle = (datum: SeriesOptionsType) => {
  // Hatching pattern
  if (datum.color && datum.color.pattern && datum.color.pattern.path) {
    const { stroke } = datum.color.pattern.path;

    return {
      background: `repeating-linear-gradient(-45deg, ${stroke}, ${stroke} 1px, transparent 1px, transparent 2px)`,
      border: `1px solid ${stroke}`,
    };
  }

  // Solid colid pattern
  return { background: datum.color };
};

const ChartLegend = ({ id, chartOptions, className, sort = true, showTotal = false, locale = LOCALE.EN_CA }: Props) => {
  const data = getData(chartOptions, sort);

  if (!data.length) {
    return null;
  }

  return (
    <div className={classNames('chart-legend', className)}>
      {data.map((datum: SeriesOptionsType) => (
        <div key={`legend-${id}-${datum.name}`} className="legend-item d-flex align-items-center">
          <div className="item-label">
            <div className="item-color" style={getThumbnailStyle(datum)} />
            <span>{datum.name}</span>
          </div>

          <div>
            <span className="percentage-field">{`${formatPercentage(datum.y, datum.yDecimalPlaces, locale)}`}</span>
          </div>
        </div>
      ))}
      {showTotal && (
        <div key={`legend-${id}-total`} className="total legend-item d-flex align-items-center">
          <div className="item-label">
            <span>
              <FormattedMessage id="Proposal.DonutChart.Legend.Total" />
            </span>
          </div>
          <div>
            <span className="percentage-field">
              {formatPercentage(
                data.reduce((acc: number, datum: SeriesOptionsType) => acc + datum.y, 0),
                0,
              )}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChartLegend;
